import images from '../../../assets/images';
import './ChatCard.scss';
import { FC, useEffect, useRef, useState } from 'react';
import { singleChatType } from '../CommonChats';
import { useAppSelector } from '../../../redux/app/hooks';
import Linkify from 'react-linkify';
import { formatFileSize, getDividerTime, getSendTime, isMessageWithinLastHour } from '../../../utils/helper';
import moment from 'moment';
import { Tooltip } from '@mui/material';
import ConfirmationPopup from '../../../components/ConfirmationPopup/ConfirmationPopup';

interface propsStyle {
  data: singleChatType;
  isLastMessage: boolean;
  highlighted: boolean;
  onClickCard: () => void;
  type: string;
  onPlayAudio: (audio: HTMLAudioElement) => void; // Callback to notify parent component
  previousData: singleChatType;
  getImage: (fileType: string) => string | undefined;
  socketRef: any;
  setReplyToMessage: any;
  replyToMessage:any;
  scrollToMessage: any;
  handleEditMessage: (data: singleChatType) => void;
}

const ChatCard: FC<propsStyle> = ({ data, isLastMessage,highlighted,type, onClickCard, onPlayAudio, previousData, getImage, socketRef, setReplyToMessage,replyToMessage,scrollToMessage, handleEditMessage }) => {
  const { userImage, docsImg, ChatReply, deleteIcon, ChatEdit, DeleteGif, ChatDeletedMessage} = images;
  const profile = useAppSelector((state) => state?.auth?.adminProfile);
  const baseUrl = process.env.REACT_APP_FRONTEND_URL;
  const audioRef = useRef<HTMLAudioElement>(null); // Ref to the audio element
  const hoverRef = useRef<HTMLDivElement>(null); // Ref to the hover container
  const [isHovered, setIsHovered] = useState(false);
  const [openOverlay, setOpenOverlay] = useState<boolean>(false);

  // Function to decorate the link with target="_blank"
  const linkDecorator = (href: string, text: string, key: number) => (
    <a href={href} key={key} target="_blank" rel="noopener noreferrer" style={{ color: '#333333', textDecoration: 'underline' }}>
      {text}
    </a>
  );

  useEffect(() => {
    const handlePlay = () => {
      if (audioRef.current) {
        onPlayAudio(audioRef.current); // Notify parent component
      }
    };

    const audioElement = audioRef.current;
    if (audioElement) {
      audioElement.addEventListener('play', handlePlay);

      return () => {
        audioElement.removeEventListener('play', handlePlay);
      };
    }
  }, [onPlayAudio]);

  // Close hover actions and overlay on outside click or scroll
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (hoverRef.current && !hoverRef.current.contains(event.target as Node)) {
        handleCloseActions();
      }
    };

    const handleScroll = () => {
      handleCloseActions();
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const sendTimestamp = getSendTime(moment(Number(data?.createdAt)).toISOString());
  const dividerTime = getDividerTime(moment(Number(data?.createdAt)).toISOString(), (previousData?.createdAt ? moment(Number(previousData?.createdAt)).toISOString() : ''))

  // Track hover state
  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  const handleCloseActions = () => {
    setIsHovered(false);
  };

  const handleChatActions = (actionData: singleChatType, action: string) => {
    if (action === "delete") {
      setOpenOverlay(true);
    }else if(action === "reply"){
      setReplyToMessage(actionData);
      handleCloseActions();
    } else if (action === "edit") {
      handleEditMessage(actionData);
    }
  }

    // click delete for me button in confirmation popup
    const onDeleteForMeButtonClick  = () => {
      setOpenOverlay(false);
      const deleteMessageData = {
        type: "deleteMessageByChatRoomId",
        data: {
          messageIds: data?.id ? [data?.id] : [],
          chatRoomId: data?.chatRoomId,
          isdeleteForMe: true
        }
      };
      if (socketRef.current && socketRef.current.readyState === 1) {
        if (data !== undefined) {
          socketRef.current.send(JSON.stringify(deleteMessageData));
        }
      }
    }

    // click delete for everyone button in confirmation popup
    const onDeleteForEveryoneButtonClick = () => {
      setOpenOverlay(false);
      const deleteMessageData = {
        type: "deleteMessageByChatRoomId",
        data: {
          messageIds: data?.id ? [data?.id] : [],
          chatRoomId: data?.chatRoomId,
          isdeleteForMe: false
        }
      };
      if (socketRef.current && socketRef.current.readyState === 1) {
        if (data !== undefined) {
          socketRef.current.send(JSON.stringify(deleteMessageData));
        }
      }
    }
  
    // click cancel button in confirmation popup
    const onNoButtonClick = () => {
      setOpenOverlay(false);
    }

    // When the reply is clicked, scroll to the original message
  const handleReplyClick = () => {
    if (data?.replyId) {
      setTimeout(() => {
        scrollToMessage(data.replyId);
      }, 100);
    } else {
      console.log('No reply ID available for scrolling.');
    }
  };

    // Reusable components
const MediaPreview = ({ src, alt, className }: { src: string; alt: string; className: string }) => (
  <img src={src} alt={alt} className={className} />
);

const VideoPreview = ({ src, className }: { src: string; className: string }) => (
  <video src={src} className={className} autoPlay />
);

const AudioPreview = ({ src, className }: { src: string; className: string }) => (
  <audio
    src={src}
    className={className}
    controls
    controlsList="nodownload noplaybackrate"
    preload="auto"
  />
);

const FilePreview = ({ src, filename, fileType }: { src: string|undefined; filename: string; fileType: string }) => (
  <div className="replyFilePreview">
    <img src={src} alt={fileType} className="replyFileIcon" />
    <span className="replyFileName">{filename}</span>
  </div>
);

    // Helper function to render content based on type
const renderContent = (content: singleChatType) => {
  const mediaUrl = baseUrl + content.message;

  switch (content.msgType) {
    case "text":
      return <span className="replyContent">{content.message}</span>;

    case "png":
      return (
        <div className="replymediaPreviewCtn">
          <MediaPreview src={mediaUrl} alt="replyImage" className="replyImagePreview" />
          <span className="replyMediaName">Sent an image</span>
        </div>
      );

    case "mp4":
      return (
        <div className="replymediaPreviewCtn">
          <VideoPreview src={mediaUrl} className="replyVideoPreview" />
          <span className="replyMediaName">Sent a video</span>
        </div>
      );

    case "mp3":
    case "3gp":
    case "webm":
    case "audio":
      return (
        <div className="replyAudioPreviewCtn">
          <span className="replyMediaName">Sent a voice message</span>
          <AudioPreview src={mediaUrl} className="replyAudioPreview" />
        </div>
      );

    case "pdf":
    case "csv":
    case "doc":
    case "docx":
    case "ppt":
    case "pptx":
    case "xls":
    case "xlsx":
    case "epub":
      return (
        <FilePreview
          src={getImage(content.msgType)}
          filename={content.fileinfo?.filename || "Unknown file"}
          fileType={content.msgType}
        />
      );

    case "combinedMessage":
      return (
        <div className="replyCombinedMessageContainer">
          {content.combinedMessage?.map((data, index) => {
            const combinedUrl = baseUrl + data?.content;
            switch (data.type) {
              case "png":
                return (
                  <div className="replymediaPreviewCtn" key={index}>
                    <MediaPreview src={combinedUrl} alt="replyImage" className="replyImagePreview" />
                    <span className="replyContent">{content.message}</span>
                  </div>
                );

              case "mp4":
                return (
                  <div className="replymediaPreviewCtn" key={index}>
                    <VideoPreview src={combinedUrl} className="replyVideoPreview" />
                    <span className="replyContent">{content.message}</span>
                  </div>
                );

              case "pdf":
              case "csv":
              case "doc":
              case "docx":
              case "ppt":
              case "pptx":
              case "xls":
              case "xlsx":
              case "epub":
                return (
                  <div key={index}>
                    <span className="replyContent">{content.message}</span>
                    <FilePreview
                      src={getImage(data.type)}
                      filename={content.fileinfo?.filename || "Unknown file"}
                      fileType={data.type}
                    />
                  </div>
                );

              default:
                return null;
            }
          })}
        </div>
      );

    default:
      return null;
  }
};

  return (

    <div className={profile?.getCurrentUser?.nodes?.[0]?.uId === data?.userId ? 'ChatCardContainer' : 'OthersChatCardContainer'}>
      <div className={dividerTime && dividerTime !== '' ? "divider" : "noDivider"}>{dividerTime}</div>
      <div className={profile?.getCurrentUser?.nodes?.[0]?.uId === data?.userId ? 'ctnFortime' : 'othersCtnFortime'}>
        {((data?.deletedForEveryOne && data?.deletedForEveryOne !== null) || (data?.deletedForMe && data?.deletedForMe !== null && data?.userId === profile?.getCurrentUser?.nodes?.[0]?.uId)) ? 
          <div className={profile?.getCurrentUser?.nodes?.[0]?.uId === data?.userId ? 'deletedMessage': 'othersDeletedMessage'}>
            <img src={ChatDeletedMessage} alt="deletedMessage" className='deletedMessageIcon'/>
            {(data?.deletedForEveryOne && data?.deletedForEveryOne !== null)?"Message deleted for everyone":"Message deleted"}
          </div>
        :
        (<div 
          className={`
            ${(isLastMessage && type === "group") ? 
              (profile?.getCurrentUser?.nodes?.[0]?.uId === data?.userId ? 'lastMessageCtn' : 'otherslastMessageCtn') :
              (isLastMessage ? 
                (profile?.getCurrentUser?.nodes?.[0]?.uId === data?.userId ? 'individualLastMessageCtn' : 'othersindividualLastMessageCtn') :
                (profile?.getCurrentUser?.nodes?.[0]?.uId === data?.userId ? 'messageCtn' : 'othersMessageCtn')
              )
            }
            ${highlighted === true? 'highlightMessageCtn' : ''}
          `}
          ref={hoverRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}>
            {(data?.replyId && data?.replyData )&& (
                  <div className="replyContainer"  onClick={handleReplyClick}>
                    <div className="replyDetails">
                      <span className="replySender">{data?.replyData?.userName}</span>
                      <span className="replyContentWrapper">{renderContent(data?.replyData)}</span>
                    </div>
                  </div>
                )}
          <div className='message' style={{ padding: data?.msgType === "text" ? '0px 15px' : '0px' }}>
            {
              (data?.msgType === "mp3" || data?.msgType === "3gp" || data?.msgType === "webm" || data?.msgType === "audio" || (data?.msgType === "combinedMessage" && (data?.combinedMessage?.[0]?.type === "mp3" || data?.combinedMessage?.[0]?.type === "3gp" || data?.combinedMessage?.[0]?.type === "webm" || data?.combinedMessage?.[0]?.type === "audio"))) ?
                <audio src={(data?.msgType === "combinedMessage" && (data?.combinedMessage?.[0]?.type === "mp3" || data?.combinedMessage?.[0]?.type === "3gp" || data?.combinedMessage?.[0]?.type === "webm" || data?.combinedMessage?.[0]?.type === "audio")) ? baseUrl + data?.combinedMessage?.[0]?.content : baseUrl + data?.message}
                  ref={audioRef} // Assign ref to audio element
                  controls
                  controlsList='nodownload noplaybackrate'
                  preload="auto"
                  className='messageAudio'
                />
                : (data?.msgType === "mp4" || (data?.msgType === "combinedMessage" && data?.combinedMessage?.[0]?.type === "mp4")) ?
                  <video src={(data?.msgType === "combinedMessage" && data?.combinedMessage?.[0]?.type === "mp4") ? baseUrl + data?.combinedMessage?.[0]?.content : baseUrl + data?.message}
                    autoPlay
                    muted
                    className='messageVideo'
                    onClick={onClickCard}
                  />
                  : (data?.msgType === "png" || (data?.msgType === "combinedMessage" && data?.combinedMessage?.[0]?.type === "png")) ?
                    <img src={(data?.msgType === "combinedMessage" && data?.combinedMessage?.[0]?.type === "png") ? baseUrl + data?.combinedMessage?.[0]?.content : baseUrl + data?.message} className='messageVideo' onClick={onClickCard} alt='messageVideo'/>
                    : (data?.msgType === "pdf" || data?.msgType === "csv" || data?.msgType === "doc" || data?.msgType === "docx" || data?.msgType === "ppt" || data?.msgType === "pptx" || data?.msgType === "xls" || data?.msgType === "xlsx" || data?.msgType === "epub" || (data?.msgType === "combinedMessage" && (data?.combinedMessage?.[0]?.type === "pdf" || data?.combinedMessage?.[0]?.type === "csv" || data?.combinedMessage?.[0]?.type === "doc" || data?.combinedMessage?.[0]?.type === "docx" || data?.combinedMessage?.[0]?.type === "ppt" || data?.combinedMessage?.[0]?.type === "pptx" || data?.combinedMessage?.[0]?.type === "xls" || data?.combinedMessage?.[0]?.type === "xlsx" || data?.combinedMessage?.[0]?.type === "epub"))) ?
                      <div className="pdfCard" onClick={onClickCard}>
                        <img
                          src={getImage(data?.msgType === "combinedMessage" ? data?.combinedMessage?.[0]?.type : data?.msgType)}
                          alt='previewImage'
                          className={`pdfPreviewImg ${getImage(data?.msgType === "combinedMessage" ? data?.combinedMessage?.[0]?.type : data?.msgType) !== docsImg ? "pdfImg" : "docsImg"}`}
                        />
                        <div className="pdfDetails">
                          <span className="pdfTitle">{data?.fileinfo?.filename || "-"}</span>
                          <span className="pdfInfo">{`${data?.fileinfo?.filesize ? formatFileSize(data?.fileinfo?.filesize) : '0MB'} .${data?.fileinfo?.filename?.split('.')?.pop()?.toUpperCase()}`}</span>
                        </div>
                      </div>
                      :
                      <Linkify componentDecorator={linkDecorator}>
                        {data?.message}
                      </Linkify>
            }
          </div>
          {
            data?.msgType === "combinedMessage" && (
              <div className='combinedMessage'>
                <Linkify componentDecorator={linkDecorator}>
                  {data?.message}
                </Linkify>
              </div>
            )
          }
          {(isLastMessage && type === "group") && <span className={profile?.getCurrentUser?.nodes?.[0]?.uId === data?.userId ? 'userName' : 'othersName'}>{data?.userName}</span>}
          {isHovered && (
            <div className={profile?.getCurrentUser?.nodes?.[0]?.uId === data?.userId ? 'hoverActions' : 'othersHoverActions'}>
              <Tooltip title="Reply" placement="top">
                <img src={ChatReply} alt="chatReply" className="replyButton" onClick={() => handleChatActions(data, "reply")} />
              </Tooltip>
              {/* <Tooltip title="Add Reaction" placement="top">
                    <img src={ChatEmoji} alt="chatEmoji" className="emojiButton" onClick={() => handleChatActions(data, "addReaction")} />
              </Tooltip> */}
              {(profile?.getCurrentUser?.nodes?.[0]?.uId === data?.userId && data?.createdAt && isMessageWithinLastHour(data?.createdAt)) && (
                  <>
                    {(data.msgType !== "mp3" && data.msgType !== "3gp" && data.msgType !== "webm" && data.msgType !== "audio") && (
                      <Tooltip title="Edit" placement="top">
                        <img src={ChatEdit} alt="edit" className="editButton" onClick={() => handleChatActions(data, "edit")} />
                      </Tooltip>
                    )}
                    <Tooltip title="Delete" placement="top">
                      <img src={deleteIcon} alt="delete" className="MoreButton" onClick={() => handleChatActions(data, "delete")} />
                    </Tooltip>
                  </>
              )}
            </div>
          )}
        </div>)
        }
        {
          data?.createdAt ?
            <div className='sendTime'><span className={(isLastMessage && type === "group") ? 'lastTimeText' : 'timeText'}> {sendTimestamp}{data?.isMessageEdited && <span className='edited'>Edited</span>}</span></div>
            : <></>
        }
      </div>
      {(isLastMessage && type === "group" &&  !((data?.deletedForEveryOne && data?.deletedForEveryOne !== null) || (data?.deletedForMe && data?.deletedForMe !== null && data?.userId === profile?.getCurrentUser?.nodes?.[0]?.uId))) &&
        <div className='avatarCtn'>
          <img src={data?.userAvatarUrl ? (baseUrl + data?.userAvatarUrl) : userImage} alt='userAvatarimage' className={data?.userAvatarUrl ? 'avatar' : 'noProfile'} />
        </div>}
        <ConfirmationPopup
        open={openOverlay}
        message={`Are you sure you want to Delete this message?`}
        gifData={DeleteGif}
        page="chat"
        deleteForMeButtonFunctionality={onDeleteForMeButtonClick}
        yesButtonFunctionality={onDeleteForEveryoneButtonClick}
        noButtonFunctionality={onNoButtonClick}
        messageWidth={'221px'}
      />
    </div>
  )
}

export default ChatCard;
